// client/extensions/SuggestEdit.ts
import { logger } from "@adornis/base/logging.js";
import { Extension, Mark, getMarkRange, getMarksBetween, isMarkActive, mergeAttributes } from "@tiptap/core";
import { Slice } from "@tiptap/pm/model";
import { TextSelection } from "@tiptap/pm/state";
import { AddMarkStep, RemoveMarkStep, ReplaceStep, Step } from "@tiptap/pm/transform";
var SuggestTranslations = {
  suggest: {
    tooltip: {
      editModeEnable: ["Bearbeitungsmodus aktivieren", "Turn on Suggest Edit mode"],
      editModeDisable: ["Bearbeitungsmodus deaktivieren", "Turn off Suggest Edit mode"],
      acceptSuggestion: ["Vorschlag annehmen", "Accept the selected suggestions"],
      rejectSuggestion: ["Vorschlag ablehnen", "Reject the selected suggestions"]
    }
  }
};
var deletionMarkName = "x-suggestedit-deletion";
var insertionMarkName = "x-suggestedit-insertion";
var formatchangeMarkName = "x-suggestedit-formatchange";
var extensionName = "suggestedit";
var suggestEditTransactionTag = "suggesteditstep";
var supportedStepTypes = [ReplaceStep, AddMarkStep, RemoveMarkStep];
var AcceptTC = "accept";
var AcceptAllTC = "accept-all";
var RejectTC = "reject";
var RejectAllTC = "reject-all";
function SuggestEditExtension(userId, username) {
  return {
    extensions: [SuggestEditTipTapExtension({ userId, username })],
    buttons: ({ editor, host }) => [
      {
        // TODO change button in some way depending on whether this extension is enabled
        name: "toggleSuggestEdit",
        icon: getSelfExt(editor).options.enabled ? "edit_off" : "edit",
        tooltip: getSelfExt(editor).options.enabled ? host.translator.translate("tiptap", "suggest", "tooltip", "editModeDisable") : host.translator.translate("tiptap", "suggest", "tooltip", "editModeEnable"),
        action: () => editor.chain().toggleTrackChangeStatus().focus().run()
      },
      ...getSelfExt(editor).options.enabled ? [] : [
        {
          name: "acceptSelected",
          icon: "check",
          tooltip: host.translator.translate("tiptap", "suggest", "tooltip", "acceptSuggestion"),
          action: () => editor.chain().acceptChange().focus().run()
        },
        {
          name: "rejectSelected",
          icon: "delete",
          tooltip: host.translator.translate("tiptap", "suggest", "tooltip", "rejectSuggestion"),
          action: () => editor.chain().rejectChange().focus().run()
        }
      ]
    ]
  };
}
function SuggestEditTipTapExtension({
  enabled = false,
  onStatusChange,
  userId,
  username,
  userTitle = username
}) {
  return Extension.create({
    name: extensionName,
    priority: 999,
    onCreate() {
      this.options.enabled = enabled;
      this.options.userId = userId;
      this.options.username = username;
      this.options.onStatusChange = onStatusChange;
      this.options.userTitle = userTitle;
      if (this.options.onStatusChange)
        this.options.onStatusChange(this.options.enabled);
    },
    addExtensions: () => [
      createCustomMark({
        name: insertionMarkName,
        extraHTMLAttributes: { style: "color: green; border-bottom: 1px solid green" }
      }),
      createCustomMark({
        name: deletionMarkName,
        extraHTMLAttributes: { style: "color: red; border-bottom: 1px solid red;" }
      }),
      createCustomMark({
        name: formatchangeMarkName,
        extraAttributes: {
          before: { type: "array", default: () => [] },
          after: { type: "array", default: () => [] }
        },
        extraHTMLAttributes: { style: "color: blue;" }
      })
    ],
    // addProseMirrorPlugins: () => [HoverProsemirrorPlugin()],
    addCommands: () => ({
      setTrackChangeStatus: (enabled2) => (param) => {
        const thisExtension = getSelfExt(param.editor);
        thisExtension.options.enabled = enabled2;
        thisExtension.options.onStatusChange?.(thisExtension.options.enabled);
        return false;
      },
      toggleTrackChangeStatus: () => (param) => {
        const thisExtension = getSelfExt(param.editor);
        thisExtension.options.enabled = !thisExtension.options.enabled;
        thisExtension.options.onStatusChanged?.(thisExtension.options.enabled);
        return false;
      },
      getTrackChangeStatus: () => (param) => {
        const thisExtension = getSelfExt(param.editor);
        return thisExtension.options.enabled;
      },
      acceptChange: () => (param) => {
        changeTrack("accept", param);
        return false;
      },
      acceptAllChanges: () => (param) => {
        changeTrack("accept-all", param);
        return false;
      },
      rejectChange: () => (param) => {
        changeTrack("reject", param);
        return false;
      },
      rejectAllChanges: () => (param) => {
        changeTrack("reject-all", param);
        return false;
      }
    }),
    // @ts-ignore
    onTransaction: ({ editor, transaction }) => {
      if (!isActionRequired(transaction))
        return;
      logger.debug(transaction, "transaction");
      const thisExtension = getSelfExt(editor);
      const trackChangeEnabled = thisExtension.options.enabled;
      const allSteps = transaction.steps.map((step) => Step.fromJSON(editor.state.doc.type.schema, step.toJSON()));
      const currentNewPos = transaction.selection.from;
      let posOffset = 0, hasAddAndDelete = false;
      allSteps.forEach((step, index, _arr) => {
        if (step instanceof ReplaceStep) {
          let delCount = 0;
          if (step.from !== step.to) {
            const slice = transaction.docs[index].slice(step.from, step.to);
            slice.content.forEach((node) => {
              if (!node.marks.find((m) => m.type.name === insertionMarkName))
                delCount += node.nodeSize;
            });
          }
          posOffset += delCount;
          const newCount = !checkSpecialCase(step, step.invert(transaction.docs[index])) && step.slice ? step.slice.size : 0;
          hasAddAndDelete = newCount > 0 && delCount > 0;
        }
      });
      if (!hasAddAndDelete)
        posOffset = 0;
      const newChangeTr = editor.state.tr;
      const applyTr = (tr) => {
        editor.view.updateState(editor.state.apply(tr));
      };
      let replayOffset = 0;
      allSteps.forEach((step, index) => {
        logger.debug(step, "step");
        if (step instanceof ReplaceStep) {
          const { nextReplayOffset } = replayReplaceStep(
            newChangeTr,
            transaction,
            step,
            index,
            editor,
            thisExtension,
            replayOffset,
            trackChangeEnabled
          );
          replayOffset = nextReplayOffset;
        } else if (trackChangeEnabled && step instanceof AddMarkStep) {
          replayAddMarkStep(newChangeTr, step, editor, thisExtension);
        } else if (trackChangeEnabled && step instanceof RemoveMarkStep) {
          replayRemoveMarkStep(newChangeTr, step, editor, thisExtension);
        }
        if (supportedStepTypes.find((t) => step instanceof t))
          applyTr(newChangeTr);
      });
      if (trackChangeEnabled) {
        const finalNewPos = currentNewPos + posOffset;
        const trWithChange = editor.view.state.tr;
        trWithChange.setSelection(TextSelection.create(editor.view.state.doc, finalNewPos));
        applyTr(trWithChange);
      }
      if (hasAddAndDelete && trackChangeEnabled) {
        editor.commands.deleteSelection();
        editor.commands.blur();
        setTimeout(() => {
          editor.commands.focus();
        }, 100);
      }
    }
  });
}
function isActionRequired(transaction) {
  return (
    // transaction actually changed the document
    transaction.docChanged && // transaction is not one of our accept/reject actions
    !transaction.getMeta(suggestEditTransactionTag) && // transaction is not a redo or undo
    !transaction.getMeta("history$") && // transaction actually has steps
    transaction.steps.length > 0 && // transaction is not one synced by another client or the server
    (!transaction.getMeta("y-sync$") || !transaction.getMeta("y-sync$").isChangeOrigin)
  );
}
function replayReplaceStep(newChangeTr, transaction, step, index, editor, thisExtension, replayOffset, trackChangeEnabled) {
  const invertedStep = step.invert(transaction.docs[index]);
  const isSpecialCase = checkSpecialCase(step, invertedStep);
  const replayStep = new ReplaceStep(
    invertedStep.from + replayOffset,
    invertedStep.from + replayOffset + (isSpecialCase ? 1 : 0),
    invertedStep.slice,
    invertedStep.toJSON().structure ?? false
  );
  if (step.slice.size && !isSpecialCase) {
    const from = step.from + replayOffset;
    const to = step.from + replayOffset + step.slice.size;
    if (trackChangeEnabled) {
      newChangeTr.addMark(
        from,
        to,
        editor.state.doc.type.schema.marks[insertionMarkName].create({
          "data-user-id": thisExtension.options.userId,
          "data-username": thisExtension.options.username,
          title: thisExtension.options.username
        })
      );
    } else {
      newChangeTr.removeMark(from, to, editor.state.doc.type.schema.marks[insertionMarkName]);
    }
    newChangeTr.removeMark(from, to, editor.state.doc.type.schema.marks[deletionMarkName]);
    newChangeTr.removeMark(from, to, editor.state.doc.type.schema.marks[formatchangeMarkName]);
  }
  if (step.from !== step.to && trackChangeEnabled) {
    const skipSteps = [];
    let addedEmptyOffset = 0;
    const travelContent = (content, parentOffset) => {
      content.forEach((node, offset) => {
        const start = parentOffset + offset;
        if (!node.isLeaf)
          return travelContent(node.content, start);
        const end = start + node.nodeSize;
        if (node.marks.find((m) => m.type.name === insertionMarkName)) {
          skipSteps.push(new ReplaceStep(start - addedEmptyOffset, end - addedEmptyOffset, Slice.empty));
          addedEmptyOffset += node.nodeSize;
          replayOffset -= node.nodeSize;
        }
        const formatchangeMark = node.marks.find((m) => m.type.name === formatchangeMarkName);
        if (formatchangeMark) {
          for (const markname of formatchangeMark.attrs.after.concat(formatchangeMarkName)) {
            const mark = node.marks.find((m) => m.type.name === markname);
            if (mark)
              skipSteps.push(new RemoveMarkStep(start - addedEmptyOffset, end - addedEmptyOffset, mark));
          }
          for (const markname of formatchangeMark.attrs.before) {
            const mark = newChangeTr.doc.type.schema.mark(markname);
            if (mark)
              skipSteps.push(new AddMarkStep(start - addedEmptyOffset, end - addedEmptyOffset, mark));
          }
        }
      });
    };
    travelContent(invertedStep.slice.content, invertedStep.from);
    replayOffset += invertedStep.slice.size;
    newChangeTr.step(replayStep);
    const { from } = replayStep;
    const to = from + replayStep.slice.size;
    newChangeTr.addMark(
      from,
      to,
      newChangeTr.doc.type.schema.marks[deletionMarkName].create({
        "data-user-id": thisExtension.options.userId,
        "data-username": thisExtension.options.username,
        title: thisExtension.options.username
      })
    );
    skipSteps.forEach((step2) => newChangeTr.step(step2));
  }
  return { nextReplayOffset: replayOffset };
}
function replayAddMarkStep(newChangeTr, step, editor, thisExtension) {
  const content = newChangeTr.doc.slice(step.from, step.to).content;
  const parentOffset = step.from;
  content.forEach((node, offset) => {
    const start = parentOffset + offset;
    const end = start + node.nodeSize;
    if (node.marks.find((m) => m.type.name === deletionMarkName)) {
      newChangeTr.removeMark(start, end, step.mark);
    } else if (!node.marks.find((m) => m.type.name === insertionMarkName)) {
      const beforeAndAfter = (existingMark2, markname) => {
        if (!existingMark2)
          return [[], [markname]];
        if (existingMark2.attrs.before.includes(markname))
          return [existingMark2.attrs.before.filter((n) => n !== markname), existingMark2.attrs.after];
        return [existingMark2.attrs.before, existingMark2.attrs.after.concat(markname)];
      };
      const existingMark = node.marks.find((m) => m.type.name === formatchangeMarkName);
      const [before, after] = beforeAndAfter(existingMark, step.mark.type.name);
      if (before.length || after.length)
        newChangeTr.addMark(
          start,
          end,
          editor.state.doc.type.schema.marks[formatchangeMarkName].create({
            "data-user-id": thisExtension.options.userId,
            "data-username": thisExtension.options.username,
            title: thisExtension.options.username,
            before,
            after
          })
        );
      else if (existingMark)
        newChangeTr.removeMark(start, end, existingMark);
    }
  });
}
function replayRemoveMarkStep(newChangeTr, step, editor, thisExtension) {
  const content = newChangeTr.doc.slice(step.from, step.to).content;
  const parentOffset = step.from;
  content.forEach((node, offset, _number) => {
    const start = parentOffset + offset;
    const end = start + node.nodeSize;
    if (node.marks.find((m) => m.type.name === deletionMarkName)) {
      newChangeTr.addMark(start, end, step.mark);
    } else if (!node.marks.find((m) => m.type.name === insertionMarkName)) {
      const beforeAndAfter = (existingMark2, markname) => {
        if (!existingMark2)
          return [[markname], []];
        if (existingMark2.attrs.after.includes(markname))
          return [existingMark2.attrs.before, existingMark2.attrs.after.filter((n) => n !== markname)];
        return [existingMark2.attrs.before.concat(markname), existingMark2.attrs.after];
      };
      const existingMark = node.marks.find((m) => m.type.name === formatchangeMarkName);
      const [before, after] = beforeAndAfter(existingMark, step.mark.type.name);
      if (before.length || after.length)
        newChangeTr.addMark(
          start,
          end,
          editor.state.doc.type.schema.marks[formatchangeMarkName].create({
            "data-user-id": thisExtension.options.userId,
            "data-username": thisExtension.options.username,
            title: thisExtension.options.username,
            before,
            after
          })
        );
      else if (existingMark)
        newChangeTr.removeMark(start, end, existingMark);
    }
  });
}
function createCustomMark({
  name,
  extraAttributes,
  extraHTMLAttributes
}) {
  return Mark.create({
    name,
    addAttributes: () => ({
      "data-user-id": {
        type: "string",
        default: () => ""
      },
      "data-username": {
        type: "string",
        default: () => ""
      },
      title: {
        type: "string",
        default: () => ""
      },
      ...extraAttributes ?? {}
    }),
    parseHTML: () => [{ tag: name }],
    renderHTML({ HTMLAttributes }) {
      return [name, mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, extraHTMLAttributes ?? {}), 0];
    }
  });
}
function changeTrack(opType, param) {
  const from = param.editor.state.selection.from;
  const to = param.editor.state.selection.to;
  let markRanges = [];
  if ((opType === AcceptTC || opType === RejectTC) && from === to) {
    let leftRange = void 0;
    if (isMarkActive(param.editor.state, insertionMarkName)) {
      leftRange = getMarkRange(
        param.editor.state.selection.$from,
        param.editor.state.doc.type.schema.marks[insertionMarkName]
      );
    } else if (isMarkActive(param.editor.state, deletionMarkName)) {
      leftRange = getMarkRange(
        param.editor.state.selection.$from,
        param.editor.state.doc.type.schema.marks[deletionMarkName]
      );
    } else if (isMarkActive(param.editor.state, formatchangeMarkName)) {
      leftRange = getMarkRange(
        param.editor.state.selection.$from,
        param.editor.state.doc.type.schema.marks[formatchangeMarkName]
      );
    }
    if (leftRange) {
      markRanges = getMarksBetween(leftRange.from, leftRange.to, param.editor.state.doc);
    }
  } else if (opType === AcceptAllTC || opType === RejectAllTC) {
    markRanges = getMarksBetween(0, param.editor.state.doc.content.size, param.editor.state.doc);
    opType = opType === AcceptAllTC ? AcceptTC : RejectTC;
  } else {
    markRanges = getMarksBetween(from, to, param.editor.state.doc);
  }
  markRanges = markRanges.filter(
    (markRange) => markRange.mark.type.name === deletionMarkName || markRange.mark.type.name === insertionMarkName || markRange.mark.type.name === formatchangeMarkName
  );
  if (!markRanges.length) {
    return false;
  }
  const currentTr = param.state.tr;
  let offset = 0;
  const isAccept = opType === AcceptTC;
  const isReject = opType === RejectTC;
  markRanges.forEach((markRange) => {
    const isInsertion = markRange.mark.type.name === insertionMarkName;
    const isDeletion = markRange.mark.type.name === deletionMarkName;
    const isFormatchange = markRange.mark.type.name === formatchangeMarkName;
    if (isReject && isFormatchange) {
      for (const markname of markRange.mark.attrs.after) {
        const mark = param.state.doc.type.schema.mark(markname);
        if (mark)
          currentTr.removeMark(markRange.from - offset, markRange.to - offset, mark);
      }
      for (const markname of markRange.mark.attrs.before) {
        const mark = param.state.doc.type.schema.mark(markname);
        if (mark)
          currentTr.addMark(markRange.from - offset, markRange.to - offset, mark);
      }
      currentTr.removeMark(markRange.from - offset, markRange.to - offset, markRange.mark);
    } else if (isAccept && isInsertion || isReject && isDeletion || isAccept && isFormatchange) {
      currentTr.removeMark(markRange.from - offset, markRange.to - offset, markRange.mark);
    } else if (isAccept && isDeletion || isReject && isInsertion) {
      currentTr.deleteRange(markRange.from - offset, markRange.to - offset);
      offset += markRange.to - markRange.from;
    }
  });
  if (currentTr.steps.length) {
    currentTr.setMeta(suggestEditTransactionTag, true);
    const newState = param.state.apply(currentTr);
    param.editor.view.updateState(newState);
  }
  return false;
}
function getSelfExt(editor) {
  return editor.extensionManager.extensions.find(
    (item) => item.type === "extension" && item.name === extensionName
  );
}
function checkSpecialCase(step, invertedStep) {
  const stepLeaves = getLeafNodes(step.slice.content);
  const invertedLeaves = getLeafNodes(invertedStep.slice.content);
  const specialCaseCharacters = [" ", "\xA0"];
  return stepLeaves.length === 1 && invertedLeaves.length === 2 && stepLeaves.every((n) => n.isText) && invertedLeaves.every((n) => n.isText) && (invertedLeaves[1]?.marks.every((m) => m.type.name === deletionMarkName) ?? false) && specialCaseCharacters.includes(stepLeaves[0]?.text ?? "not in array") && specialCaseCharacters.includes(invertedLeaves[1]?.text ?? "not in array");
}
function getLeafNodes(content) {
  const leaves = [];
  content.forEach((node) => {
    if (node.isLeaf)
      leaves.push(node);
    else
      leaves.push(...getLeafNodes(node.content));
  });
  return leaves;
}
export {
  SuggestEditExtension,
  SuggestTranslations
};
